import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  ContractMachineListType,
  ContractMachinesResponse,
  CreateContractMachineRequest,
  MachineInformationType,
  UpdateContractMachineRequest,
  ContractMachineContractOptionType,
} from "../../../types/ContractMachine";
import {
  getContractMachines,
  getContractMachineDetail,
  createContractMachine,
  updateContractMachine,
  deleteContractMachine,
  copyMachinesFromTemplate,
} from "../../../services/ContractMachinesService";
import UserContractSelect from "../UserContractSelect";

interface ContractMachinesPageProps {
  onBack?: () => void;
  initialData?: ContractMachinesResponse | null;
  onDataLoaded?: (data: ContractMachinesResponse) => void;
}

interface FormState {
  name: string;
  description: string;
  machineInformation: MachineInformationType[];
}

const emptyMachineInfo: MachineInformationType = {
  machineName: "",
  machineType: "",
  machineClass: "",
  machineIdentification: "",
};

const emptyForm: FormState = {
  name: "",
  description: "",
  machineInformation: [],
};

const isForbiddenError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 403;

export default function ContractMachinesPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<ContractMachinesPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 512,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();
  const canManage = checkAccess("ADMIN_PRICES");

  const [machines, setMachines] = useState<ContractMachineListType[]>(
    initialData?.machines ?? [],
  );
  const [availableContracts, setAvailableContracts] = useState<
    ContractMachineContractOptionType[]
  >(initialData?.availableContracts ?? []);
  const [showCopyFromTemplate, setShowCopyFromTemplate] = useState(
    initialData?.showCopyFromTemplate ?? false,
  );
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    initialData?.selectedContractId ?? null,
  );
  const [machinesLoading, setMachinesLoading] = useState(false);
  const loadSeqRef = useRef(0);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingMachineId, setEditingMachineId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [copySourceContractId, setCopySourceContractId] = useState<
    string | null
  >(null);
  const [copying, setCopying] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const showError = useCallback(
    (fallback: string, error: unknown) => {
      console.error(error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : fallback);
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message,
        color: "red",
      });
    },
    [t],
  );

  const publishData = useCallback(
    (
      contractId: number | null,
      nextMachines: ContractMachineListType[],
      showCopy: boolean,
    ) => {
      onDataLoaded?.({
        availableContracts,
        selectedContractId: contractId,
        machines: nextMachines,
        showCopyFromTemplate: showCopy,
      });
    },
    [onDataLoaded, availableContracts],
  );

  useEffect(() => {
    if (!canManage) {
      setAccessDenied(true);
      return;
    }
    setAccessDenied(false);
  }, [canManage]);

  useEffect(() => {
    if (!initialData) return;
    setSelectedContractId(initialData.selectedContractId ?? null);
    setMachines(initialData.machines);
    setShowCopyFromTemplate(initialData.showCopyFromTemplate);
    setAvailableContracts(initialData.availableContracts);
  }, [initialData]);

  const loadMachinesForContract = useCallback(
    async (contractId: number) => {
      const seq = ++loadSeqRef.current;
      setMachinesLoading(true);
      try {
        const result = await getContractMachines(
          selectedAddressSystemId,
          contractId,
        );
        if (seq !== loadSeqRef.current) return;

        setMachines(result.machines);
        setShowCopyFromTemplate(result.showCopyFromTemplate);
        setAvailableContracts(result.availableContracts);
        publishData(contractId, result.machines, result.showCopyFromTemplate);
      } catch (error) {
        if (seq !== loadSeqRef.current) return;

        if (isForbiddenError(error)) {
          setAccessDenied(true);
        } else {
          showError("Failed to load equipment for contract", error);
        }
      } finally {
        if (seq === loadSeqRef.current) {
          setMachinesLoading(false);
        }
      }
    },
    [publishData, selectedAddressSystemId, showError],
  );

  const handleContractChange = (contractId: number | null) => {
    loadSeqRef.current += 1;
    setSelectedContractId(contractId);
    if (contractId) {
      void loadMachinesForContract(contractId);
      return;
    }

    setMachinesLoading(false);
    setMachines([]);
    setShowCopyFromTemplate(false);
    publishData(null, [], false);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setModalOpened(false);
    setEditingMachineId(null);
    setForm(emptyForm);
    setNameError(null);
  };

  const openCreate = () => {
    setEditingMachineId(null);
    setForm({
      ...emptyForm,
      machineInformation: [
        { ...emptyMachineInfo },
        { ...emptyMachineInfo },
        { ...emptyMachineInfo },
      ],
    });
    setNameError(null);
    setModalOpened(true);
  };

  const openEdit = async (machine: ContractMachineListType) => {
    try {
      const detail = await getContractMachineDetail(
        selectedAddressSystemId,
        machine.id,
      );
      setEditingMachineId(machine.id);

      // Add existing machine information plus 3 empty rows
      const existingInfo = detail.machineInformation ?? [];
      const emptyRows = [
        { ...emptyMachineInfo },
        { ...emptyMachineInfo },
        { ...emptyMachineInfo },
      ];

      setForm({
        name: detail.name,
        description: detail.description ?? "",
        machineInformation: [...existingInfo, ...emptyRows],
      });
      setNameError(null);
      setModalOpened(true);
    } catch (error) {
      showError("Failed to load machine details", error);
    }
  };

  const buildRequest = ():
    | CreateContractMachineRequest
    | UpdateContractMachineRequest => {
    // Filter out empty machine information rows
    const filteredInfo = form.machineInformation.filter(
      (info) => info.machineName && info.machineName.trim().length > 0,
    );

    return {
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      machineInformation: filteredInfo,
    };
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setNameError(t("MACHINES_NAME_REQUIRED", "Name is required"));
      return;
    }
    if (!selectedContractId) return;

    setSaving(true);
    try {
      const request = buildRequest();
      const currentEditingId = editingMachineId;

      if (currentEditingId) {
        await updateContractMachine(
          selectedAddressSystemId,
          selectedContractId,
          currentEditingId,
          request as UpdateContractMachineRequest,
        );
        setMachines((prev) => {
          const nextMachines = prev.map((m) =>
            m.id === currentEditingId ? { ...m, name: request.name } : m,
          );
          publishData(selectedContractId, nextMachines, false);
          return nextMachines;
        });
        notifications.show({
          title: t("MACHINES_SUCCESS", "Success"),
          message: t("MACHINES_UPDATED", "Equipment updated"),
          color: "green",
        });
      } else {
        const newId = await createContractMachine(
          selectedAddressSystemId,
          selectedContractId,
          request as CreateContractMachineRequest,
        );
        const newMachine: ContractMachineListType = {
          id: newId,
          name: request.name,
          isInUse: false,
        };
        setMachines((prev) => {
          const updatedMachines = [...prev, newMachine].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          setShowCopyFromTemplate(false);
          publishData(selectedContractId, updatedMachines, false);
          return updatedMachines;
        });
        notifications.show({
          title: t("MACHINES_SUCCESS", "Success"),
          message: t("MACHINES_CREATED", "Equipment created"),
          color: "green",
        });
      }

      closeModal(true);
    } catch (error) {
      showError(
        editingMachineId
          ? "Failed to update equipment"
          : "Failed to create equipment",
        error,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (machine: ContractMachineListType) => {
    if (!selectedContractId) return;
    try {
      await deleteContractMachine(
        selectedAddressSystemId,
        selectedContractId,
        machine.id,
      );
      setMachines((prev) => {
        const nextMachines = prev.filter((m) => m.id !== machine.id);
        const showCopy = nextMachines.length === 0;
        setShowCopyFromTemplate(showCopy);
        publishData(selectedContractId, nextMachines, showCopy);
        return nextMachines;
      });
      notifications.show({
        title: t("MACHINES_SUCCESS", "Success"),
        message: t("MACHINES_DELETED", "Equipment deleted"),
        color: "green",
      });
    } catch (error) {
      showError("Failed to delete equipment", error);
    }
  };

  const confirmDelete = (machine: ContractMachineListType) => {
    modals.openConfirmModal({
      title: t("MACHINES_DELETE_TITLE", "Delete equipment"),
      children: (
        <Text size="sm">
          {t(
            "MACHINES_DELETE_CONFIRMATION",
            `Delete equipment "${machine.name}"?`,
          )}
        </Text>
      ),
      labels: {
        confirm: t("COMMON_DELETE", "Delete"),
        cancel: t("COMMON_CANCEL", "Cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void handleDelete(machine);
      },
    });
  };

  const handleCopyFromTemplate = async () => {
    if (!copySourceContractId || !selectedContractId) return;

    setCopying(true);
    try {
      const count = await copyMachinesFromTemplate(selectedAddressSystemId, {
        sourceContractId: parseInt(copySourceContractId, 10),
        targetContractId: selectedContractId,
      });

      if (count > 0) {
        notifications.show({
          title: t("MACHINES_SUCCESS", "Success"),
          message: t("MACHINES_COPIED", `Copied ${count} equipment item(s)`),
          color: "green",
        });
        void loadMachinesForContract(selectedContractId);
      } else {
        notifications.show({
          title: t("MACHINES_INFO", "Info"),
          message: t("MACHINES_NO_ITEMS_COPIED", "No new items were copied"),
          color: "blue",
        });
      }
    } catch (error) {
      showError("Failed to copy equipment from template", error);
    } finally {
      setCopying(false);
      setCopySourceContractId(null);
    }
  };

  const updateMachineInfo = (
    index: number,
    field: keyof MachineInformationType,
    value: string,
  ) => {
    setForm((f) => {
      const updated = [...f.machineInformation];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, machineInformation: updated };
    });
  };

  return (
    <Stack h="100%" gap="md">
      <Group justify="space-between">
        <Text fw={700}>{t("TEXT_LINK_MACHINES", "Equipment (machines)")}</Text>
        {canManage &&
          !accessDenied &&
          selectedContractId &&
          !showCopyFromTemplate && (
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              {t("MACHINES_ADD", "Add equipment")}
            </Button>
          )}
      </Group>

      {accessDenied ? (
        <Text c="dimmed" size="sm">
          {t(
            "SETTINGS_NO_LINKS_FOR_ROLE",
            "No settings links match your permissions.",
          )}
        </Text>
      ) : (
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <UserContractSelect
            contracts={availableContracts}
            label={t("MACHINES_SELECT_CONTRACT", "Contract")}
            placeholder={t(
              "MACHINES_SELECT_CONTRACT_PLACEHOLDER",
              "Select a contract",
            )}
            emptyHint={t(
              "MACHINES_NO_CONTRACTS",
              "No contracts available for this address system.",
            )}
            value={selectedContractId}
            onChange={handleContractChange}
          />

          {!selectedContractId ? (
            <Text c="dimmed" size="sm">
              {t(
                "MACHINES_SELECT_CONTRACT_FIRST",
                "Select a contract to manage its equipment.",
              )}
            </Text>
          ) : machinesLoading ? (
            <Group justify="center" style={{ flex: 1 }}>
              <Loader size="sm" />
            </Group>
          ) : showCopyFromTemplate ? (
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={500}>
                  {t(
                    "MACHINES_COPY_FROM_TEMPLATE",
                    "Copy equipment from existing contract",
                  )}
                </Text>
                <Select
                  label={t("MACHINES_SOURCE_CONTRACT", "Source contract")}
                  placeholder={t(
                    "MACHINES_SELECT_SOURCE",
                    "Select a contract to copy from",
                  )}
                  data={availableContracts
                    .filter((contract) => contract.id !== selectedContractId)
                    .map((contract) => ({
                      value: contract.id.toString(),
                      label: contract.name,
                    }))}
                  value={copySourceContractId}
                  onChange={setCopySourceContractId}
                />
                <Group>
                  <Button
                    onClick={() => void handleCopyFromTemplate()}
                    loading={copying}
                    disabled={!copySourceContractId}
                  >
                    {t("MACHINES_COPY_BUTTON", "Copy")}
                  </Button>
                  <Button variant="default" onClick={openCreate}>
                    {t("MACHINES_CREATE_NEW", "Create new instead")}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ) : machines.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t(
                "MACHINES_EMPTY",
                "No equipment found. Add one to get started.",
              )}
            </Text>
          ) : (
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("MACHINES_COL_NAME", "Name")}</Table.Th>
                    {canManage && (
                      <Table.Th style={{ width: 96 }}>
                        {t("MACHINES_COL_ACTIONS", "Actions")}
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {machines.map((machine) => (
                    <Table.Tr key={machine.id}>
                      <Table.Td>{machine.name}</Table.Td>
                      {canManage && (
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              aria-label={t("COMMON_EDIT", "Edit")}
                              onClick={() => void openEdit(machine)}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t("COMMON_DELETE", "Delete")}
                              onClick={() => confirmDelete(machine)}
                              disabled={machine.isInUse}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={() => closeModal()}
        title={
          editingMachineId
            ? t("MACHINES_EDIT_TITLE", "Edit equipment")
            : t("MACHINES_ADD_TITLE", "Add equipment")
        }
        size="xl"
      >
        <Stack>
          <TextInput
            label={t("MACHINES_NAME", "Name")}
            value={form.name}
            error={nameError}
            required
            onChange={(event) => {
              setForm((f) => ({ ...f, name: event.currentTarget.value }));
              setNameError(null);
            }}
          />
          <Textarea
            label={t("MACHINES_DESCRIPTION", "Editable equipment")}
            value={form.description ?? ""}
            onChange={(event) =>
              setForm((f) => ({
                ...f,
                description: event.currentTarget.value,
              }))
            }
            minRows={3}
            autosize
            maxRows={8}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t("MACHINES_CLASSIFIED_EQUIPMENT", "Classified equipment")}
            </Text>
            <Box
              style={{
                maxHeight: 300,
                overflow: "auto",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Table striped>
                <Table.Thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "var(--mantine-color-gray-3)",
                  }}
                >
                  <Table.Tr>
                    <Table.Th style={{ minWidth: 120 }}>
                      {t("MACHINES_DEVICE", "Device")}
                    </Table.Th>
                    <Table.Th style={{ minWidth: 120 }}>
                      {t("MACHINES_TYPE", "Type")}
                    </Table.Th>
                    <Table.Th style={{ minWidth: 120 }}>
                      {t("MACHINES_CLASS", "Class")}
                    </Table.Th>
                    <Table.Th style={{ minWidth: 120 }}>
                      {t("MACHINES_REGISTRATION", "Registration")}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {form.machineInformation.map((info, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={info.machineName ?? ""}
                          onChange={(e) =>
                            updateMachineInfo(
                              index,
                              "machineName",
                              e.currentTarget.value,
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={info.machineType ?? ""}
                          onChange={(e) =>
                            updateMachineInfo(
                              index,
                              "machineType",
                              e.currentTarget.value,
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={info.machineClass ?? ""}
                          onChange={(e) =>
                            updateMachineInfo(
                              index,
                              "machineClass",
                              e.currentTarget.value,
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={info.machineIdentification ?? ""}
                          onChange={(e) =>
                            updateMachineInfo(
                              index,
                              "machineIdentification",
                              e.currentTarget.value,
                            )
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          </Stack>

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => closeModal()}
              disabled={saving}
            >
              {t("COMMON_CANCEL", "Cancel")}
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              {t("COMMON_SAVE", "Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
